/* ==========================================================================
   Crumpaw · WanderPup Engraved Collar — buy box controller.

   One custom element, <cp-wp-buybox>, drives all three layouts the prototype
   shipped (guided, quick, story). The layouts differ only in which controls
   are on the page, so every ref lookup here is allowed to come back null and
   every write is guarded. That is deliberate: it is the reason three very
   different templates share one script instead of three near-copies drifting
   apart.

   Degradation contract, same as the harness and the leather collar page: if
   anything in here throws, the form still sells a collar. Colour and size are
   real radios inside the form with a <noscript> <select name="id"> fallback,
   the name and phone inputs carry their own properties[...] names, and Add to
   cart is never left disabled by a preview error.

   Money is never invented. Every price the shopper reads is either a string
   Liquid already ran through `money`, or cents from the real variant put
   through the shop's own money format. The script chooses between values; it
   does not price anything.

   Multi-collar packs. A pack tier says "two dogs, two names", so each extra
   collar gets its own name and phone and becomes its own cart line with its
   own properties. The lead collar goes in on the form itself; the rest follow
   on the next cart:update, and roll back together if any of them fails — a
   cart holding one collar when the shopper was shown a two-collar price is
   worse than a cart holding nothing, because nothing on the page would say
   so. The pack price is display only; the discount that actually charges it
   lives in Shopify admin as an automatic discount on quantity.
   ========================================================================== */

(() => {
  'use strict';

  if (typeof window === 'undefined' || !window.customElements) return;

  if (customElements.get('cp-wp-buybox')) return;

  const CART_UPDATE = 'cart:update';
  const SOURCE = 'cp-wp';

  /* Letters, digits, space, apostrophe, hyphen, plus accented Latin. "Zoë"
     arriving at the laser as "Zo" is the worst failure this page has, because
     it is silent: the customer sees their name in the field and not on the
     plate, and the collar ships wrong. Latin-1 Supplement and Latin Extended-A
     cover the European names a customer here will actually type, minus the two
     maths signs that share that block. */
  const NAME_ALLOWED = /[^A-Za-z0-9 '\-À-ÖØ-öø-ſ]/g;
  const PHONE_ALLOWED = /[^0-9 +()\-]/g;

  const qs = (root, name) => root.querySelector(`[data-cp-wp="${name}"]`);
  const qsa = (root, name) => Array.from(root.querySelectorAll(`[data-cp-wp="${name}"]`));

  const setText = (node, value) => {
    if (node) node.textContent = value == null ? '' : String(value);
  };

  const show = (node, on) => {
    if (node) node.hidden = !on;
  };

  /**
   * Minimal Shopify money formatter: cents plus a format string such as
   * "${{amount}}". Falls back to a plain dollar rendering if the format string
   * is unusable, which is better than printing "NaN" at the shopper.
   */
  const formatMoney = (cents, format) => {
    const amount = Number(cents) || 0;

    const withDelimiters = (value, precision, thousands, decimal) => {
      const fixed = (value / 100).toFixed(precision);
      const parts = fixed.split('.');
      const grouped = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${grouped}${decimal}${parts[1]}` : grouped;
    };

    if (typeof format !== 'string' || !format.includes('{{')) {
      return `$${withDelimiters(amount, 2, ',', '.')}`;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, token) => {
      switch (token) {
        case 'amount':
          return withDelimiters(amount, 2, ',', '.');
        case 'amount_no_decimals':
          return withDelimiters(amount, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(amount, 2, "'", '.');
        default:
          return withDelimiters(amount, 2, ',', '.');
      }
    });
  };

  const interpolate = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
    );

  /**
   * Rewrite a field to exactly what will be engraved: disallowed characters
   * dropped, and the name upper-cased the way the plate draws it.
   *
   * The preview is a claim about the finished collar. If the plate reads
   * COOPER and the line item property reads "cooper#", the customer approved
   * one thing and the laser is handed another, and nothing anywhere would say
   * so. Cheaper to make the field tell the truth than to reconcile it later.
   *
   * The caret is put back where it was, less whatever was removed in front of
   * it, so correcting a typo mid-string does not throw the cursor to the end.
   */
  const sanitiseField = (input, allowed, upper) => {
    if (!input) return;
    const raw = String(input.value || '');
    let next = raw.replace(allowed, '');
    if (upper) next = next.toUpperCase();
    if (next === raw) return;
    const caret = input.selectionStart;
    const removed = raw.length - next.length;
    input.value = next;
    if (caret !== null && input.setSelectionRange) {
      try {
        input.setSelectionRange(Math.max(0, caret - removed), Math.max(0, caret - removed));
      } catch (error) {
        /* number and email inputs throw on setSelectionRange; nothing to do */
      }
    }
  };

  const readCart = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  /**
   * Take one line back out of the cart by its key. By key, not by variant id:
   * the shopper may already have had that variant in the cart before any of
   * this, and removing theirs would be worse than the problem being fixed.
   */
  const removeLine = (key) =>
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    }).then((r) => r.json());

  const addToCart = (items) =>
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((response) =>
        response.json().then((data) => {
          if (!response.ok) {
            const error = new Error(
              data && data.description ? data.description : 'Cart request failed'
            );
            error.status = response.status;
            throw error;
          }
          return data;
        })
      )
      .then((data) => {
        document.dispatchEvent(
          new CustomEvent(CART_UPDATE, { bubbles: true, detail: { data: { source: SOURCE } } })
        );
        return data;
      });

  class CpWpBuybox extends HTMLElement {
    connectedCallback() {
      if (this.dataset.cpWpReady === 'true') return;

      const configEl = qs(this, 'config');
      if (!configEl) return;
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (error) {
        return;
      }
      if (!this.config || !Array.isArray(this.config.variants)) return;

      this.dataset.cpWpReady = 'true';
      this.copy = this.config.copy || {};
      this.moneyFormat = this.config.moneyFormat || '${{amount}}';

      this.refs = {
        variantId: qs(this, 'variant-id'),
        quantity: qs(this, 'quantity'),
        cta: qs(this, 'cta'),
        ctaHint: qs(this, 'cta-hint'),
        error: qs(this, 'error'),
        stageTrack: qs(this, 'stage-track'),
        caption: qs(this, 'caption'),
        summary: qs(this, 'summary'),
        nameInput: qs(this, 'name'),
        nameCount: qs(this, 'name-count'),
        nameHint: qs(this, 'name-hint'),
        phoneInput: qs(this, 'phone'),
        phoneCount: qs(this, 'phone-count'),
        nameOnly: qs(this, 'name-only'),
        propFont: qs(this, 'prop-font'),
        propColour: qs(this, 'prop-colour'),
        propSize: qs(this, 'prop-size'),
        propPack: qs(this, 'prop-pack'),
        tierNote: qs(this, 'tier-note'),
        extras: qs(this, 'extras'),
        leashToggle: qs(this, 'leash-toggle'),
        leashPicker: qs(this, 'leash-picker'),
        leashMatch: qs(this, 'leash-match'),
        leashCard: qs(this, 'leash-card'),
        leashTitle: qs(this, 'leash-title'),
        leashSub: qs(this, 'leash-sub'),
        leashCollarDot: qs(this, 'leash-collar-dot'),
        leashModal: qs(this, 'leash-modal'),
        rail: qs(this, 'rail'),
        railPrev: qs(this, 'rail-prev'),
        railNext: qs(this, 'rail-next'),
        lightbox: qs(this, 'lightbox'),
        lightboxImg: qs(this, 'lightbox-img'),
        lightboxHint: qs(this, 'lightbox-hint'),
        sizeModal: qs(this, 'size-modal'),
        sizeModalBody: qs(this, 'size-modal-body'),
        fontLabel: qs(this, 'font-label'),
      };

      /* Lists, because a layout may render the same value in more than one
         place — the story card shows the price twice, the closing CTA shows
         the total again. */
      this.lists = {
        priceNow: qsa(this, 'price-now'),
        priceWas: qsa(this, 'price-was'),
        priceSave: qsa(this, 'price-save'),
        priceEach: qsa(this, 'price-each'),
        ctaLabel: qsa(this, 'cta-label'),
        colourLabel: qsa(this, 'colour-label'),
        sizeLabel: qsa(this, 'size-label'),
        plate: qsa(this, 'plate'),
        plateName: qsa(this, 'plate-name'),
        platePhone: qsa(this, 'plate-phone'),
        /* Every image that shows the chosen colour. The stage's colour slide
           cannot carry data-cp-wp="product-thumb" as well — it already needs
           data-cp-wp="slide" for the snap track — so it is marked
           data-cp-wp-colour-img and both spellings are collected here. */
        productThumb: Array.from(
          this.querySelectorAll('[data-cp-wp="product-thumb"], [data-cp-wp-colour-img]')
        ),
        thumbs: qsa(this, 'thumb'),
        slides: qsa(this, 'slide'),
        leashImg: qsa(this, 'leash-img'),
        leashLabel: qsa(this, 'leash-label'),
        leashNote: qsa(this, 'leash-note'),
      };

      this.colourInputs = qsa(this, 'colour');
      this.sizeInputs = qsa(this, 'size');
      this.fontInputs = qsa(this, 'font');
      this.tierInputs = qsa(this, 'tier');
      this.leashInputs = qsa(this, 'leash-colour');
      this.extraRows = qsa(this, 'extra-row');

      this.state = {
        /* The rail opens on the first gallery photo — a dog wearing the
           collar — the way the prototype did. Picking a colour swings it over
           to that colour's product shot and the last thumb goes current. */
        media: (this.config.gallery && this.config.gallery[0]
          ? this.config.gallery[0].key
          : 'colour'),
        leashOn: false,
        leashModal: false,
        sizeModal: false,
        leashPicked: false,
      };

      this.pending = null;
      this.wire();
      this.wireRail();
      this.wireStage();
      this.wireLightbox();
      this.wireVideos();
      this.render();
    }

    disconnectedCallback() {
      if (this.onCartUpdate) document.removeEventListener(CART_UPDATE, this.onCartUpdate);
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      if (this.railObserver) this.railObserver.disconnect();
      if (this.videoObserver) this.videoObserver.disconnect();
      if (this.syncRail) window.removeEventListener('resize', this.syncRail);
      document.body.classList.remove('cp-wp-locked');
    }

    /* ------------------------------------------------------------ selection */

    checked(inputs) {
      return inputs.find((input) => input.checked) || null;
    }

    colour() {
      const input = this.checked(this.colourInputs);
      return input ? input.value : (this.config.variants[0] || {}).colour || '';
    }

    size() {
      const input = this.checked(this.sizeInputs);
      return input ? input.value : (this.config.variants[0] || {}).size || '';
    }

    colourMeta() {
      const value = String(this.colour()).toLowerCase();
      return (
        (this.config.colours || []).find((c) => String(c.value).toLowerCase() === value) ||
        (this.config.colours || [])[0] ||
        {}
      );
    }

    fontMeta() {
      const input = this.checked(this.fontInputs);
      const id = input ? input.value : null;
      return (
        (this.config.fonts || []).find((f) => f.id === id) || (this.config.fonts || [])[0] || {}
      );
    }

    tier() {
      const input = this.checked(this.tierInputs);
      const id = input ? input.value : null;
      const tiers = this.config.tiers || [];
      return tiers.find((t) => t.id === id) || tiers[0] || { id: 'one', quantity: 1, saving: 0 };
    }

    variant() {
      const colour = String(this.colour()).toLowerCase();
      const size = String(this.size()).toLowerCase();
      return (
        this.config.variants.find(
          (v) => String(v.colour).toLowerCase() === colour && String(v.size).toLowerCase() === size
        ) || null
      );
    }

    leash() {
      const options = this.config.leash && this.config.leash.options ? this.config.leash.options : [];
      const input = this.checked(this.leashInputs);
      const value = input ? input.value : null;
      return options.find((o) => o.value === value) || options[0] || null;
    }

    /**
     * The lead the collar suggests. Exact colour name first, then a name that
     * contains it as a word — the lead product calls its pink "Hot Pink" —
     * then black, which goes with anything.
     *
     * Returns null rather than guessing when nothing fits: there is no yellow
     * lead, and shipping an orange one against a yellow collar because the
     * two are adjacent on a colour wheel would be a substitution nobody asked
     * for. The picker just says which colour is actually selected.
     */
    suggestedLeash() {
      const options = this.config.leash && this.config.leash.options ? this.config.leash.options : [];
      const pool = options.filter((o) => o.available !== false);
      const choices = pool.length ? pool : options;
      const collar = String((this.colourMeta() || {}).label || '').toLowerCase().trim();
      if (!collar || !choices.length) return null;
      return (
        choices.find((o) => String(o.value).toLowerCase() === collar) ||
        choices.find((o) => String(o.value).toLowerCase().split(/\s+/).indexOf(collar) !== -1) ||
        choices.find((o) => String(o.value).toLowerCase() === 'black') ||
        null
      );
    }

    /** Follow the collar until the shopper chooses a lead colour themselves. */
    syncLeashDefault() {
      if (this.state.leashPicked || !this.leashInputs.length) return;
      const want = this.suggestedLeash();
      if (!want) return;
      const input = this.leashInputs.find((i) => i.value === want.value);
      if (input && !input.checked && !input.disabled) input.checked = true;
    }

    /* --------------------------------------------------------------- fields */

    name() {
      if (!this.refs.nameInput) return '';
      return String(this.refs.nameInput.value || '')
        .replace(NAME_ALLOWED, '')
        .slice(0, this.config.nameMax || 10);
    }

    phone() {
      if (!this.refs.phoneInput || this.nameOnly()) return '';
      return String(this.refs.phoneInput.value || '')
        .replace(PHONE_ALLOWED, '')
        .slice(0, this.config.phoneMax || 12);
    }

    nameOnly() {
      return !!(this.refs.nameOnly && this.refs.nameOnly.checked);
    }

    /* ----------------------------------------------------------------- wire */

    wire() {
      const rerender = () => this.render();

      this.addEventListener('change', (event) => {
        const target = event.target;
        if (!target) return;
        /* A colour change swings the stage over to that colour's photo, the
           way picking a colour in the prototype did. Any other change leaves
           whichever image the shopper chose from the thumb rail alone. */
        if (this.colourInputs.includes(target)) this.state.media = 'colour';
        if (this.leashInputs.includes(target)) this.state.leashPicked = true;
        if (target === this.refs.leashToggle) {
          this.state.leashOn = target.checked;
          if (!this.state.leashOn) this.state.leashModal = false;
        }
        rerender();
      });

      this.addEventListener('input', (event) => {
        const target = event.target;
        if (!target) return;
        const isName =
          target === this.refs.nameInput || target.matches('[data-cp-wp="extra-name"]');
        const isPhone =
          target === this.refs.phoneInput || target.matches('[data-cp-wp="extra-phone"]');
        if (!isName && !isPhone) return;
        sanitiseField(target, isName ? NAME_ALLOWED : PHONE_ALLOWED, isName);
        rerender();
      });

      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-cp-wp="thumb"]');
        if (thumb) {
          event.preventDefault();
          this.state.media = thumb.dataset.mediaKey || 'colour';
          this.render();
          return;
        }
        if (event.target.closest('[data-cp-wp="size-open"]')) {
          event.preventDefault();
          this.openSizeGuide();
          return;
        }
        if (
          event.target.closest('[data-cp-wp="size-close"]') ||
          event.target.matches('[data-cp-wp="size-modal"]')
        ) {
          event.preventDefault();
          this.state.sizeModal = false;
          this.render();
          return;
        }
        /* Picking a size from the chart inside the dialog answers the question
           the dialog was opened to answer, so it closes itself. */
        if (
          this.state.sizeModal &&
          event.target.closest('[data-cp-wp-size-pick]') &&
          this.refs.sizeModal &&
          this.refs.sizeModal.contains(event.target)
        ) {
          this.state.sizeModal = false;
          this.render();
          return;
        }
        if (event.target.closest('[data-cp-wp="leash-open"]')) {
          event.preventDefault();
          this.state.leashModal = true;
          this.render();
          return;
        }
        if (
          event.target.closest('[data-cp-wp="leash-close"]') ||
          event.target.matches('[data-cp-wp="leash-modal"]')
        ) {
          event.preventDefault();
          this.state.leashModal = false;
          this.render();
        }
      });

      this.onKeydown = (event) => {
        if (event.key !== 'Escape') return;
        /* The lightbox sits above the other two, so it is dismissed first. */
        if (this.refs.lightbox && !this.refs.lightbox.hidden) {
          if (this.closeLightbox) this.closeLightbox();
          return;
        }
        if (this.state.leashModal || this.state.sizeModal) {
          this.state.leashModal = false;
          this.state.sizeModal = false;
          this.render();
        }
      };
      document.addEventListener('keydown', this.onKeydown);

      /* Add to cart is blocked, not disabled, while the name is empty: a
         disabled button fires no click and there would be nothing to hang the
         "here is the field you missed" behaviour on. */
      if (this.refs.cta) {
        this.refs.cta.addEventListener(
          'click',
          (event) => {
            if (this.refs.cta.getAttribute('aria-disabled') !== 'true') {
              this.pending = this.followLines();
              return;
            }
            event.preventDefault();
            event.stopImmediatePropagation();
            this.nudgeName();
          },
          true
        );
      }

      /* Horizon's own form posts the lead collar. Everything else follows on
         the cart:update it raises, so this never has to reach inside
         <product-form-component>. */
      this.onCartUpdate = (event) => {
        const source = event.detail && event.detail.data && event.detail.data.source;
        if (source === SOURCE) return;
        const follow = this.pending;
        this.pending = null;
        if (follow && follow.length) this.addRest(follow);
      };
      document.addEventListener(CART_UPDATE, this.onCartUpdate);

      /* The size table lower down the page picks a size up here. */
      this.onSelect = (event) => {
        const detail = event.detail || {};
        if (detail.section && detail.section !== this.config.sectionId) return;
        if (detail.size) {
          const input = this.sizeInputs.find(
            (i) => String(i.value).toLowerCase() === String(detail.size).toLowerCase()
          );
          if (input && !input.disabled) {
            input.checked = true;
            this.render();
            this.scrollIntoBuy();
          }
        }
      };
      document.addEventListener('cpwp:select', this.onSelect);
    }

    /**
     * Open the size guide over the buy box.
     *
     * The chart is cloned from the cp-wp-sizes section rather than duplicated
     * into this section's settings, so there is one set of measurements on the
     * page and no way for the dialog and the table to disagree. Cloned once
     * and kept: the copy is inert markup and the source never changes.
     *
     * If that section is not on the template there is nothing to show, so the
     * button does what it used to do and scrolls to the anchor — a dead
     * control would be worse than the old behaviour.
     */
    openSizeGuide() {
      const body = this.refs.sizeModalBody;
      if (!body || !this.refs.sizeModal) return this.scrollToSizes();

      if (!body.firstElementChild) {
        const source = document.querySelector('[data-cp-wp="size-chart"]');
        if (!source) return this.scrollToSizes();
        const copy = source.cloneNode(true);
        copy.removeAttribute('data-cp-wp');
        body.appendChild(copy);
      }

      this.state.sizeModal = true;
      this.render();
      const close = this.refs.sizeModal.querySelector('[data-cp-wp="size-close"]');
      if (close) close.focus();
      return undefined;
    }

    scrollToSizes() {
      const target = document.getElementById('cp-wp-sizes');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return undefined;
    }

    /**
     * The gallery lightbox: click the stage photo to see it big, click the big
     * one to magnify.
     *
     * The magnified image scales about the point that was clicked, which means
     * the origin has to be worked out from the image's UNSCALED box. Once
     * `transform: scale()` is on, getBoundingClientRect() reports the scaled
     * box, so the rect is captured at the moment of zooming and reused for
     * every later pointer move - reading it live walks the origin away from
     * the pointer.
     *
     * A video slide has no <img>, so it is left alone: tapping a video should
     * play it, not open a still of it.
     */
    wireLightbox() {
      const box = this.refs.lightbox;
      const img = this.refs.lightboxImg;
      if (!box || !img) return;

      let baseRect = null;

      const setHint = (zoomed) => {
        if (!this.refs.lightboxHint) return;
        const text = zoomed ? this.copy.zoomHintOut : this.copy.zoomHint;
        setText(this.refs.lightboxHint, text || '');
        this.refs.lightboxHint.hidden = !text;
      };

      const unzoom = () => {
        baseRect = null;
        box.dataset.zoom = 'false';
        img.style.transformOrigin = '50% 50%';
        setHint(false);
      };

      const originFrom = (event) => {
        const rect = baseRect || img.getBoundingClientRect();
        const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
        const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
        return `${x}% ${y}%`;
      };

      const close = () => {
        box.hidden = true;
        unzoom();
        document.body.classList.remove('cp-wp-locked');
        if (this.lightboxOpener && this.lightboxOpener.focus) {
          this.lightboxOpener.focus({ preventScroll: true });
        }
        this.lightboxOpener = null;
      };
      this.closeLightbox = close;

      this.addEventListener('click', (event) => {
        const slide = event.target.closest('[data-cp-wp="slide"]');
        if (!slide || box.contains(event.target)) return;
        const source = slide.tagName === 'IMG' ? slide : slide.querySelector('img');
        if (!source) return;
        event.preventDefault();
        img.src = source.dataset.cpWpFull || source.currentSrc || source.src;
        img.alt = source.alt || '';
        this.lightboxOpener = source;
        box.hidden = false;
        unzoom();
        document.body.classList.add('cp-wp-locked');
        const button = qs(box, 'lightbox-close');
        if (button) button.focus();
      });

      box.addEventListener('click', (event) => {
        if (event.target === img) {
          if (box.dataset.zoom === 'true') {
            unzoom();
            return;
          }
          baseRect = img.getBoundingClientRect();
          img.style.transformOrigin = originFrom(event);
          box.dataset.zoom = 'true';
          setHint(true);
          return;
        }
        /* The backdrop and the close button both dismiss it. */
        close();
      });

      box.addEventListener('pointermove', (event) => {
        if (box.dataset.zoom !== 'true') return;
        img.style.transformOrigin = originFrom(event);
      });
    }

    /**
     * The gallery rail's arrows.
     *
     * They page by whatever is actually visible rather than a fixed number of
     * thumbs, so the same code is right for a 595px column and a 360px phone.
     * The whole control hides itself when nothing overflows — a product with
     * three photos should not be offered arrows that do nothing — and each
     * arrow hides at its end of the track rather than sitting there greyed
     * out, because a rail that ends is not an error state.
     */
    wireRail() {
      const rail = this.refs.rail;
      if (!rail) return;

      const page = (direction) => {
        const step = Math.max(rail.clientWidth * 0.8, 120);
        rail.scrollBy({ left: step * direction, behavior: 'smooth' });
      };
      if (this.refs.railPrev) this.refs.railPrev.addEventListener('click', () => page(-1));
      if (this.refs.railNext) this.refs.railNext.addEventListener('click', () => page(1));

      this.syncRail = () => {
        /* Sub-pixel layout means scrollLeft rarely lands exactly on 0 or on
           the maximum, so both ends need a tolerance. */
        const slack = 2;
        const overflows = rail.scrollWidth - rail.clientWidth > slack;
        show(this.refs.railPrev, overflows && rail.scrollLeft > slack);
        show(
          this.refs.railNext,
          overflows && rail.scrollLeft + rail.clientWidth < rail.scrollWidth - slack
        );
      };

      rail.addEventListener('scroll', this.syncRail, { passive: true });
      if (typeof ResizeObserver === 'function') {
        this.railObserver = new ResizeObserver(this.syncRail);
        this.railObserver.observe(rail);
      } else {
        window.addEventListener('resize', this.syncRail);
      }
      this.syncRail();
    }

    nudgeName() {
      const input = this.refs.nameInput;
      if (!input) return;
      input.focus();
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    scrollIntoBuy() {
      const card = qs(this, 'buy');
      if (card) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /* --------------------------------------------------------------- render */

    render() {
      const variant = this.variant();
      const colour = this.colourMeta();
      const font = this.fontMeta();
      const tier = this.tier();
      const quantity = Number(tier.quantity) || 1;
      const name = this.name();
      const nameOnly = this.nameOnly();
      const phone = this.phone();

      /* Sizes that do not exist in this colour are struck through rather than
         hidden, so the row does not reflow under the finger that is choosing. */
      this.sizeInputs.forEach((input) => {
        const match = this.config.variants.find(
          (v) =>
            String(v.colour).toLowerCase() === String(this.colour()).toLowerCase() &&
            String(v.size).toLowerCase() === String(input.value).toLowerCase()
        );
        input.disabled = !match || !match.available;
      });

      if (this.refs.variantId && variant) this.refs.variantId.value = variant.id;
      if (this.refs.quantity) this.refs.quantity.value = String(quantity);

      this.syncLeashDefault();
      const leash = this.leash();

      /* ---- price. Cents come from the real variant; the tier saving is a
         setting, and the discount that charges it lives in admin. The leash is
         its own cart line, so a ticked one joins both the total and the struck
         total at its own price: the saving stays the collar's, as on the
         blossom, reflective and harness pages. */
      const unit = variant ? Number(variant.price) : 0;
      const compare = variant ? Number(variant.compareAt) || 0 : 0;
      const saving = Number(tier.saving) || 0;
      const leashPrice = this.state.leashOn && leash ? Number(leash.price) || 0 : 0;
      const collarTotal = Math.max(0, unit * quantity - saving);
      const total = collarTotal + leashPrice;
      const wasTotal = compare > unit ? compare * quantity + leashPrice : 0;

      this.lists.priceNow.forEach((node) =>
        setText(node, formatMoney(total, this.moneyFormat))
      );
      this.lists.priceWas.forEach((node) => {
        show(node, wasTotal > 0);
        setText(node, formatMoney(wasTotal, this.moneyFormat));
      });
      this.lists.priceSave.forEach((node) => {
        const off = (wasTotal > 0 ? wasTotal - total : saving);
        show(node, off > 0);
        setText(
          node,
          interpolate(this.copy.save || 'You save {amount} ({percent})', {
            amount: formatMoney(off, this.moneyFormat),
            /* Carries its own % sign, as it already does on the blossom and
               reflective controllers, so a badge template reads "({percent})"
               and not "({percent}%)". The base is what the saving comes off,
               not the price being paid. */
            percent: `${Math.round((off / (wasTotal > 0 ? wasTotal : total + off)) * 100) || 0}%`,
          })
        );
      });
      this.lists.priceEach.forEach((node) => {
        show(node, quantity > 1);
        setText(
          node,
          interpolate(this.copy.each || '{amount} each for {count} collars', {
            amount: formatMoney(Math.round(collarTotal / quantity), this.moneyFormat),
            count: quantity,
          })
        );
      });

      /* ---- tier prices in the list itself */
      this.tierInputs.forEach((input) => {
        const meta = (this.config.tiers || []).find((t) => t.id === input.value);
        if (!meta) return;
        const node = this.querySelector(`[data-cp-wp="tier-price"][data-tier="${meta.id}"]`);
        if (!node) return;
        const tierTotal = Math.max(0, unit * (Number(meta.quantity) || 1) - (Number(meta.saving) || 0));
        setText(node, formatMoney(tierTotal, this.moneyFormat));
      });

      /* ---- gallery */
      this.showSlide(this.state.media);
      this.lists.productThumb.forEach((img) => {
        if (colour.image && img.getAttribute('src') !== colour.image) img.src = colour.image;
      });
      this.lists.thumbs.forEach((button) => {
        const on = (button.dataset.mediaKey || 'colour') === this.state.media;
        button.setAttribute('aria-current', String(on));
        /* Choosing a colour selects the last tile on the rail, which on a
           long gallery is off-screen. Bring it into view so the highlight is
           something the shopper can actually see. */
        if (on && this.refs.rail && button.parentElement === this.refs.rail) {
          const left = button.offsetLeft;
          const right = left + button.offsetWidth;
          const view = this.refs.rail.scrollLeft;
          if (left < view || right > view + this.refs.rail.clientWidth) {
            this.refs.rail.scrollTo({ left: left - 8, behavior: 'smooth' });
          }
        }
      });

      setText(
        this.refs.caption,
        interpolate(this.copy.caption || '{colour} · Size {size}', {
          colour: colour.label || this.colour(),
          size: this.size(),
        })
      );
      setText(
        this.refs.summary,
        interpolate(this.copy.summary || '{colour} · {size}', {
          colour: colour.label || this.colour(),
          size: this.size(),
        })
      );
      this.lists.colourLabel.forEach((node) => setText(node, colour.label || this.colour()));
      this.lists.sizeLabel.forEach((node) => setText(node, this.size()));
      setText(this.refs.fontLabel, font.label || '');

      /* ---- the plate */
      const plateName = (name || this.copy.platePlaceholderName || 'YOUR DOG').toUpperCase();
      const platePhone = nameOnly ? '' : phone || this.copy.platePlaceholderPhone || '';
      this.lists.plate.forEach((node) => {
        node.dataset.finish = colour.finish || 'steel';
      });
      this.lists.plateName.forEach((node) => {
        setText(node, plateName);
        node.style.fontFamily = font.family || '';
        node.style.fontWeight = font.weight || '';
      });
      this.lists.platePhone.forEach((node) => {
        setText(node, platePhone);
        node.hidden = !platePhone;
        node.style.fontFamily = font.family || '';
        node.style.fontWeight = font.weight || '';
      });

      if (this.refs.phoneInput) this.refs.phoneInput.disabled = nameOnly;
      setText(this.refs.nameCount, `${name.length}/${this.config.nameMax || 10}`);
      setText(
        this.refs.phoneCount,
        nameOnly ? this.copy.phoneNotEngraved || 'Not engraved' : `${phone.length}/${this.config.phoneMax || 12}`
      );
      if (this.refs.nameHint) {
        this.refs.nameHint.dataset.state = name ? 'ok' : 'empty';
        setText(this.refs.nameHint, name ? this.copy.hintOk || '' : this.copy.hintEmpty || '');
      }

      /* ---- properties travel with the order, so the packing slip reads back
         without a lookup */
      if (this.refs.propFont) this.refs.propFont.value = font.label || '';
      if (this.refs.propColour) this.refs.propColour.value = colour.label || this.colour();
      if (this.refs.propSize) this.refs.propSize.value = this.size();
      if (this.refs.propPack) {
        this.refs.propPack.value = quantity > 1 ? tier.label || `${quantity} collars` : '';
        this.refs.propPack.disabled = quantity <= 1;
      }

      /* ---- extra collars in a pack */
      this.extraRows.forEach((row) => {
        const index = Number(row.dataset.index) || 0;
        const applies = index < quantity - 1;
        row.hidden = !applies;
        row.querySelectorAll('input').forEach((input) => {
          input.disabled = !applies;
        });
      });
      show(this.refs.tierNote, quantity > 1);

      /* ---- leash */
      show(this.refs.leashPicker, this.state.leashOn);
      if (this.refs.leashToggle) this.refs.leashToggle.checked = this.state.leashOn;
      if (this.refs.leashCard) {
        this.refs.leashCard.dataset.on = String(this.state.leashOn);
      }
      if (leash) {
        this.lists.leashImg.forEach((img) => {
          if (img.getAttribute('src') !== leash.image) img.src = leash.image;
          img.alt = interpolate(this.copy.leashAlt || '{colour} bungee lead', {
            colour: leash.label,
          });
        });
        this.lists.leashLabel.forEach((node) => setText(node, leash.label));

        /* The card states what it is before it is added and what was added
           after — "add a matching lead" is an offer, "lead added, black,
           ships as its own item" is a receipt, and they are not the same
           sentence. */
        setText(
          this.refs.leashTitle,
          this.state.leashOn ? this.copy.leashTitleOn || '' : this.copy.leashTitleOff || ''
        );
        setText(
          this.refs.leashSub,
          this.state.leashOn
            ? interpolate(this.copy.leashSubOn || '', { colour: leash.label })
            : this.copy.leashSubOff || ''
        );

        /* The chip names the collar, not the lead: it is there so the shopper
           can see what they are matching against while they choose. */
        setText(
          this.refs.leashMatch,
          interpolate(this.copy.leashCollarChip || '{colour}', { colour: colour.label || '' })
        );
        if (this.refs.leashCollarDot) {
          this.refs.leashCollarDot.style.background = colour.dot || 'transparent';
        }
      }
      if (this.refs.leashModal) {
        this.refs.leashModal.hidden = !this.state.leashModal;
        this.refs.leashModal.setAttribute('aria-hidden', String(!this.state.leashModal));
      }
      if (this.refs.sizeModal) {
        this.refs.sizeModal.hidden = !this.state.sizeModal;
        this.refs.sizeModal.setAttribute('aria-hidden', String(!this.state.sizeModal));
      }
      document.body.classList.toggle(
        'cp-wp-locked',
        this.state.leashModal || this.state.sizeModal
      );

      /* ---- the button */
      const sellable = !!(variant && variant.available);
      const ready = sellable && !!name;
      if (this.refs.cta) {
        this.refs.cta.setAttribute('aria-disabled', String(!ready));
        /* Blocked and sold out both carry aria-disabled, but only one of them
           has a field to send the shopper to. The stylesheet has no way to
           tell them apart from that attribute alone, so name the state here
           and let it keep sold out looking like the dead end it is. */
        this.refs.cta.dataset.state = !sellable ? 'soldout' : ready ? 'ready' : 'blocked';
        this.lists.ctaLabel.forEach((node) =>
          setText(
            node,
            !sellable
              ? this.copy.ctaSoldOut || 'Sold out'
              : !name
                ? this.copy.ctaBlocked || 'Add their name to continue'
                : interpolate(this.copy.ctaReady || 'Add to cart — {amount}', {
                    amount: formatMoney(total, this.moneyFormat),
                  })
          )
        );
      }
      if (this.refs.ctaHint) {
        this.refs.ctaHint.dataset.state = ready ? 'ok' : 'empty';
        setText(this.refs.ctaHint, ready ? this.copy.ctaHintOk || '' : this.copy.ctaHintEmpty || '');
      }

      this.painted = true;
    }

    /**
     * Scroll the stage to the slide for `key`.
     *
     * Guarded against the scroll handler it triggers: without the flag, a
     * programmatic scroll reports back as a user scroll, sets the state to
     * where it already is, and the two chase each other.
     */
    showSlide(key) {
      const track = this.refs.stageTrack;
      if (!track) return;
      const slide = this.lists.slides.find((el) => el.dataset.mediaKey === key);
      if (!slide) return;
      if (Math.abs(track.scrollLeft - slide.offsetLeft) < 2) return;
      this.autoScrolling = true;
      window.clearTimeout(this.autoScrollTimer);
      this.autoScrollTimer = window.setTimeout(() => {
        this.autoScrolling = false;
      }, 600);
      /* `auto` on the very first paint: a smooth scroll from slide 0 on load
         is a slide show nobody asked for. */
      track.scrollTo({ left: slide.offsetLeft, behavior: this.painted ? 'smooth' : 'auto' });
    }

    /**
     * Swiping the stage is the browser's own scrolling, so all this has to do
     * is read back which slide came to rest under the frame and tell the rest
     * of the component about it.
     */
    wireStage() {
      const track = this.refs.stageTrack;
      if (!track) return;

      let idle;
      track.addEventListener(
        'scroll',
        () => {
          if (this.autoScrolling) return;
          window.clearTimeout(idle);
          idle = window.setTimeout(() => {
            const middle = track.scrollLeft + track.clientWidth / 2;
            let nearest = null;
            let best = Infinity;
            this.lists.slides.forEach((el) => {
              const distance = Math.abs(el.offsetLeft + el.offsetWidth / 2 - middle);
              if (distance < best) {
                best = distance;
                nearest = el;
              }
            });
            if (!nearest) return;
            const key = nearest.dataset.mediaKey;
            if (key && key !== this.state.media) {
              this.state.media = key;
              this.render();
            }
          }, 90);
        },
        { passive: true }
      );
    }

    /**
     * Gallery video: play it while it is the slide on screen, pause it the
     * moment it is not.
     *
     * The autoplay attribute on its own starts the video the moment the page
     * loads, while it is sitting several slides off to the side of a track
     * nobody has scrolled yet — a video running behind the frame, burning a
     * phone's data and battery on something not being watched. The observer
     * roots on the track, so "visible" means visible in the gallery rather
     * than anywhere in the document.
     *
     * play() returns a promise that rejects when the browser declines, which
     * is not an error worth surfacing: the video is muted and inline so it
     * should be granted, and where it is not the shopper still has controls.
     */
    wireVideos() {
      /* Queried by the attribute the markup actually carries. It was
         `qsa(this, 'autoplay')` — i.e. [data-cp-wp="autoplay"] — but the slide
         needs data-cp-wp="slide" for the snap track and marks itself
         data-cp-wp-autoplay instead, so this matched nothing and the observer
         below was never built. The video then only ever played if the browser
         granted bare autoplay, which on desktop it does not for a slide that
         starts off to the side of the track. */
      const videos = Array.from(this.querySelectorAll('video[data-cp-wp-autoplay]'));
      if (!videos.length) return;

      /* This used to bail out entirely under prefers-reduced-motion, which on
         an iPhone with Reduce Motion switched on meant the clip never played
         at all — the shopper opened the video slide and got a still. A muted,
         silent product loop is not the motion that setting exists to suppress,
         and WCAG 2.2.2 asks for a way to STOP auto-playing motion rather than
         for it never to start: these videos carry `controls`, so there is one.
         If the browser or the OS refuses anyway — iOS Low Power Mode does,
         and no site can override it — the catch below leaves the controls
         showing rather than a dead frame. */
      const track = this.refs.stageTrack;
      if (!track || !('IntersectionObserver' in window)) return;

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting) {
              video.muted = true;
              const started = video.play();
              if (started && started.catch) {
                started.catch(() => {
                  video.controls = true;
                });
              }
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { root: track, threshold: 0.6 }
      );

      videos.forEach((video) => {
        video.pause();
        this.videoObserver.observe(video);
      });
    }

    /* ------------------------------------------------------------- checkout */

    /** Everything that is not the lead collar, as /cart/add.js line items. */
    followLines() {
      const items = [];
      const tier = this.tier();
      const quantity = Number(tier.quantity) || 1;
      const variant = this.variant();
      const font = this.fontMeta();
      const colour = this.colourMeta();

      if (variant && quantity > 1) {
        this.extraRows.forEach((row) => {
          const index = Number(row.dataset.index) || 0;
          if (index >= quantity - 1) return;
          const nameEl = row.querySelector('[data-cp-wp="extra-name"]');
          const phoneEl = row.querySelector('[data-cp-wp="extra-phone"]');
          const onlyEl = row.querySelector('[data-cp-wp="extra-name-only"]');
          const extraName = String((nameEl && nameEl.value) || '')
            .replace(NAME_ALLOWED, '')
            .slice(0, this.config.nameMax || 10)
            .toUpperCase();
          const extraOnly = !!(onlyEl && onlyEl.checked);
          const extraPhone = extraOnly
            ? ''
            : String((phoneEl && phoneEl.value) || '')
                .replace(PHONE_ALLOWED, '')
                .slice(0, this.config.phoneMax || 12);
          const properties = {};
          properties[this.config.keys.name] = extraName;
          properties[this.config.keys.phone] = extraPhone;
          properties[this.config.keys.font] = font.label || '';
          properties[this.config.keys.colour] = colour.label || this.colour();
          properties[this.config.keys.size] = this.size();
          properties[this.config.keys.pack] = tier.label || `${quantity} collars`;
          items.push({ id: variant.id, quantity: 1, properties });
        });
      }

      const leash = this.leash();
      if (this.state.leashOn && leash && leash.id) {
        items.push({ id: leash.id, quantity: 1 });
      }

      return items;
    }

    /**
     * The rest of the order goes in as its own lines after the form's own add
     * has landed. If any of it fails, the lead collar comes back out: a cart
     * holding one collar when the shopper was shown a pack price is worse than
     * a cart holding nothing, because the price they agreed to and the price
     * they would pay no longer match and nothing on the page would say so.
     */
    addRest(items) {
      const variant = this.variant();
      readCart()
        .then((cart) => {
          const line = (cart.items || []).find(
            (item) => variant && String(item.variant_id) === String(variant.id)
          );
          const key = line ? line.key : null;
          return addToCart(items).catch((error) => {
            if (!key) throw error;
            return removeLine(key).then(() => {
              throw error;
            });
          });
        })
        .then(() => setText(this.refs.error, ''))
        .catch(() => {
          setText(this.refs.error, this.copy.addFailed || '');
          document.dispatchEvent(
            new CustomEvent(CART_UPDATE, { bubbles: true, detail: { data: { source: SOURCE } } })
          );
        });
    }
  }

  customElements.define('cp-wp-buybox', CpWpBuybox);

  /* =========================================================================
     The review cards' "read it in full" dialog.

     Each card clamps its body to three lines — which is also what keeps a row
     of cards the same height whatever length the reviews are — and the whole
     card is a button that opens the review at full size, photo first.

     The dialog shows a *clone* of the card rather than a second copy of the
     same block settings, so the popup and the card can never disagree about
     what a reviewer said. Nothing here reaches into the buy box.
     ========================================================================= */
  const RV = (name) => `[data-cp-wp-rv="${name}"]`;

  class CpWpRail extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector(RV('modal'));
      this.body = this.querySelector(RV('modal-body'));
      this.opener = null;

      this.addEventListener('click', (event) => {
        const target = event.target;
        if (!target || !target.closest) return;
        const open = target.closest(RV('open'));
        if (open) {
          event.preventDefault();
          this.open(open.closest(RV('card')), open);
          return;
        }
        if (target.closest(RV('close')) || target === this.modal) {
          event.preventDefault();
          this.close();
        }
      });

      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.modal && !this.modal.hidden) this.close();
      };
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      document.body.classList.remove('cp-wp-locked');
    }

    open(card, opener) {
      if (!card || !this.modal || !this.body) return;
      const copy = card.cloneNode(true);
      copy.removeAttribute('data-cp-wp-rv');
      /* The block id belongs to the card the theme editor already knows
         about; a second element carrying it would give it two targets. */
      copy.removeAttribute('data-shopify-editor-block');
      copy.querySelectorAll('[data-cp-wp-rv], .cp-wp-review__more').forEach((node) => node.remove());
      this.body.textContent = '';
      this.body.appendChild(copy);

      this.opener = opener || null;
      this.modal.hidden = false;
      document.body.classList.add('cp-wp-locked');
      const close = this.modal.querySelector(RV('close'));
      if (close) close.focus();
    }

    close() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.body.classList.remove('cp-wp-locked');
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }
  }

  customElements.define('cp-wp-rail', CpWpRail);

  /* =========================================================================
     The size table lower down the page. It is its own section, so it talks to
     the buy box through an event rather than a reference.
     ========================================================================= */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cp-wp-size-pick]');
    if (!button) return;
    event.preventDefault();
    document.dispatchEvent(
      new CustomEvent('cpwp:select', {
        bubbles: true,
        detail: { size: button.getAttribute('data-cp-wp-size-pick') },
      })
    );
  });

  /* =========================================================================
     Videos outside the buy box — the "how it goes on" clip in cp-wp-demo.

     Same contract as the gallery's, and for the same reason: `autoplay` alone
     is a request, and desktop browsers routinely decline it for a clip that
     is below the fold when the page loads. Nothing ever started it again once
     it scrolled into view, so it sat on its poster. Playing it when it is on
     screen and pausing it when it is not is both reliable and what a shopper
     expects — and it does not burn a phone's battery on a clip nobody is
     looking at.
     ========================================================================= */
  const wireStandaloneVideos = () => {
    if (!('IntersectionObserver' in window)) return;
    const videos = Array.from(document.querySelectorAll('video[data-cp-wp-autoplay]')).filter(
      (video) => !video.closest('cp-wp-buybox') && !video.dataset.cpWpObserved
    );
    if (!videos.length) return;

    /* This clip has no `controls` — it is a silent loop, not a player — so a
       refused play() would leave a poster the shopper cannot start. Whatever
       the reason for the refusal (iOS Low Power Mode is the common one, and no
       site can override it), the controls appear so the video is still
       watchable. Reduce Motion is deliberately NOT a blanket block here: it
       left the clip frozen on an iPhone with the setting on, and a muted
       product loop with a pause control is what WCAG 2.2.2 asks for. */
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.muted = true;
            const started = video.play();
            if (started && started.catch) {
              started.catch(() => {
                video.controls = true;
              });
            }
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.35 }
    );

    videos.forEach((video) => {
      video.dataset.cpWpObserved = '1';
      observer.observe(video);
    });
  };

  wireStandaloneVideos();
  /* Re-run for a section the theme editor drops in after load. */
  document.addEventListener('shopify:section:load', wireStandaloneVideos);

  /* The closing CTA and any "personalise yours" button scroll to the buy box. */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cp-wp-scroll-buy]');
    if (!button) return;
    event.preventDefault();
    const target = document.querySelector('[data-cp-wp="buy"]');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
})();
